import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Package, ShoppingBag, Sparkles, CreditCard, User, LogOut, Settings, Gift, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import NotificationBell from "../components/NotificationBell";
import OrderTracking from "../components/OrderTracking";
import Navbar from "../components/Navbar";

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Failed to fetch orders");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "text-[#3E6656] bg-[#3E6656]/10";
      case "shipped": return "text-[#6B7A8F] bg-[#6B7A8F]/10";
      case "delivered": return "text-[#20C997] bg-[#20C997]/10";
      case "pending": return "text-[#D4A373] bg-[#D4A373]/10";
      default: return "text-[#5C5D5C] bg-[#5C5D5C]/10";
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Navbar */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1B1A]">
              Welcome, {user?.name}
            </h1>
            <p className="text-[#5C5D5C] mt-1">Manage your orders and AI generations</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="dashboard-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#5C5D5C] text-sm">Total Orders</p>
                <p className="text-3xl font-light text-[#1A1B1A] mt-1" data-testid="total-orders">{orders.length}</p>
              </div>
              <Package size={24} className="text-[#20C997]" />
            </div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#5C5D5C] text-sm">AI Generations</p>
                <p className="text-3xl font-light text-[#1A1B1A] mt-1">{user?.ai_generations_used || 0}/4</p>
              </div>
              <Sparkles size={24} className="text-[#F06570]" />
            </div>
          </div>

          <div className="dashboard-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#5C5D5C] text-sm">Subscription</p>
                <p className="text-xl font-light text-[#1A1B1A] mt-1">
                  {user?.subscription_tier ? user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1) : "Free"}
                </p>
              </div>
              <CreditCard size={24} className="text-[#3E6656]" />
            </div>
          </div>

          <div className="dashboard-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/subscription")}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#5C5D5C] text-sm">Upgrade Plan</p>
                <p className="text-sm font-medium text-[#20C997] mt-1">Get Unlimited AI</p>
              </div>
              <Settings size={24} className="text-[#5C5D5C]" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Button 
            onClick={() => navigate("/ai-visualizer")}
            className="h-auto py-8 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none flex flex-col items-center gap-2"
            data-testid="ai-visualizer-btn"
          >
            <Sparkles size={32} />
            <span className="text-lg font-medium">AI Room Visualiser</span>
            <span className="text-sm opacity-80">{user?.subscription_tier ? "Unlimited" : `${4 - (user?.ai_generations_used || 0)} free left`}</span>
          </Button>

          <Button 
            onClick={() => navigate("/ai-chat")}
            variant="outline"
            className="h-auto py-8 border-[#20C997] text-[#20C997] rounded-none flex flex-col items-center gap-2"
            data-testid="ai-chat-btn"
          >
            <User size={32} />
            <span className="text-lg font-medium">Design Consultant</span>
            <span className="text-sm opacity-80">Chat with AI</span>
          </Button>

          <Button 
            onClick={() => navigate("/products")}
            variant="outline"
            className="h-auto py-8 border-[#F06570] text-[#F06570] rounded-none flex flex-col items-center gap-2"
            data-testid="shop-btn"
          >
            <ShoppingBag size={32} />
            <span className="text-lg font-medium">Shop Products</span>
            <span className="text-sm opacity-80">Browse catalogue</span>
          </Button>

          <Button 
            onClick={() => navigate("/referral")}
            variant="outline"
            className="h-auto py-8 border-[#3E6656] text-[#3E6656] rounded-none flex flex-col items-center gap-2"
            data-testid="referral-btn"
          >
            <Gift size={32} />
            <span className="text-lg font-medium">Referral Programme</span>
            <span className="text-sm opacity-80">Earn 10% credit</span>
          </Button>
        </div>

        {/* Orders */}
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mb-6">
            Recent Orders
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="dashboard-card text-center py-12">
              <Package size={48} className="mx-auto text-[#8E8F8E] mb-4" />
              <p className="text-[#5C5D5C]">No orders yet</p>
              <Button 
                onClick={() => navigate("/products")}
                className="mt-4 bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
              >
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((order, index) => (
                <div key={order.id} className="dashboard-card" data-testid={`order-${index}`}>
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <div>
                      <p className="font-medium text-[#1A1B1A]">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-[#5C5D5C] mt-1">
                        {new Date(order.created_at).toLocaleDateString("en-GB")} • {order.items?.length} items
                      </p>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div>
                        <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <p className="text-lg font-medium text-[#1A1B1A] mt-2">£{order.total?.toFixed(2)}</p>
                      </div>
                      {expandedOrder === order.id ? <ChevronUp size={18} className="text-[#5C5D5C]" /> : <ChevronDown size={18} className="text-[#5C5D5C]" />}
                    </div>
                  </div>
                  {expandedOrder === order.id && (
                    <div className="mt-6 pt-6 border-t border-[#EAE6DF]">
                      <OrderTracking orderId={order.id} currentStatus={order.status} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
